/**
 * Pressure units with exact factors in pascals.
 *
 * - **Bar and pascal multiples.** 1 bar = 100 000 Pa exactly; 1 mbar = 1 hPa = 100 Pa.
 * - **Standard atmosphere.** 1 atm = 101 325 Pa exactly (adopted by the 10th CGPM, 1954),
 *   and the technical atmosphere 1 at = 1 kgf/cm² = 98 066.5 Pa exactly, from standard
 *   gravity 9.80665 m/s².
 * - **Torr.** 1 Torr = 1/760 atm = 101 325/760 Pa = 133.3223684… Pa, a repeating decimal, so
 *   it is held as a ratio.
 * - **Millimetre of mercury.** The conventional value is 13 595.1 kg/m³ × 9.80665 m/s² ×
 *   0.001 m = 133.322387415 Pa exactly. It differs from the Torr by about one part in seven
 *   million, so the two are interchangeable in practice but not by definition. The inch of
 *   mercury is 25.4 conventional millimetres.
 * - **Pounds per square inch.** 1 psi = 1 lbf/in², with the pound-force 0.45359237 kg ×
 *   9.80665 m/s² and the inch 0.0254 m: 6894.757293168… Pa, again a repeating decimal.
 * - **Barye.** 1 Ba = 1 dyn/cm² = 0.1 Pa (CGS).
 *
 * The values are those of NIST Special Publication 811 (2008 edition), Appendix B.
 */

import {
  decimal,
  divide,
  multiply,
  type Ratio,
  ratio,
} from "@/lib/tool-kit/calendars/exact";

export interface PressureUnitDef {
  id: string;
  symbol: string;
  pascals: Ratio;
}

const POUND_FORCE = multiply(decimal("0.45359237"), decimal("9.80665"));
const SQUARE_INCH = multiply(decimal("0.0254"), decimal("0.0254"));
const MMHG = decimal("133.322387415");

export const PRESSURE_UNITS: readonly PressureUnitDef[] = [
  { id: "pa", symbol: "Pa", pascals: decimal("1") },
  { id: "hpa", symbol: "hPa", pascals: decimal("100") },
  { id: "kpa", symbol: "kPa", pascals: decimal("1000") },
  { id: "mpa", symbol: "MPa", pascals: decimal("1000000") },
  { id: "bar", symbol: "bar", pascals: decimal("100000") },
  { id: "mbar", symbol: "mbar", pascals: decimal("100") },
  { id: "atm", symbol: "atm", pascals: decimal("101325") },
  { id: "at", symbol: "at", pascals: decimal("98066.5") },
  {
    id: "torr",
    symbol: "Torr",
    pascals: ratio(BigInt(101_325), BigInt(760)),
  },
  { id: "mmhg", symbol: "mmHg", pascals: MMHG },
  { id: "inhg", symbol: "inHg", pascals: multiply(MMHG, decimal("25.4")) },
  { id: "psi", symbol: "psi", pascals: divide(POUND_FORCE, SQUARE_INCH) },
  { id: "ba", symbol: "Ba", pascals: decimal("0.1") },
];
